<template>
  <div class="min-h-[60vh] lg:min-h-0" data-testid="reservations-page">
    <header class="mb-6 lg:mb-8">
      <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1">
          <h2 class="text-2xl font-bold tracking-tight text-charcoal dark:text-white lg:text-3xl">
            {{ $t('app.reservations') }}
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('app.reservationsSubtitle') }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            class="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            :title="$t('app.reservationsHowItWorksTooltip')"
            :aria-label="$t('app.reservationsHowItWorksTooltip')"
            data-testid="reservations-page-how-it-works"
            @click="openHowItWorks"
          >
            <span class="material-icons" aria-hidden="true">help_outline</span>
          </button>
        </div>
      </div>
    </header>

    <div v-if="loadingRestaurants || !ownerStore.hasFetched" class="space-y-3">
      <div v-for="i in 2" :key="i" class="h-20 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 animate-pulse" />
    </div>

    <div
      v-else-if="ownerStore.hasFetched && !restaurants.length"
      class="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-8 text-center"
    >
      <span class="material-icons text-5xl text-slate-300 dark:text-slate-600">event_available</span>
      <p class="mt-4 text-slate-500 dark:text-slate-400">{{ $t('app.reservationsCreateFirst') }}</p>
      <router-link to="/app/welcome?openWizard=1" class="mt-6 inline-block">
        <AppButton variant="primary" class="min-h-[44px]">{{ $t('app.addRestaurant') }}</AppButton>
      </router-link>
    </div>

    <template v-else>
      <div class="space-y-6">
        <div class="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-6">
          <label for="reservations-restaurant-select" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('app.reservationsRestaurantFilter') }}
          </label>
          <select
            id="reservations-restaurant-select"
            v-model="selectedRestaurantUuid"
            class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-zinc-800 text-charcoal dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
            :aria-label="$t('app.reservationsRestaurantFilter')"
          >
            <option value="">
              {{ $t('app.reservationsSelectRestaurant') }}
            </option>
            <option
              v-for="r in restaurants"
              :key="r.uuid"
              :value="r.uuid"
            >
              {{ r.name }}
              <template v-if="r.pending_reservations_count > 0">
                ({{ $t('app.reservationsPendingCount', { n: r.pending_reservations_count }) }})
              </template>
            </option>
          </select>
        </div>

        <template v-if="selectedRestaurant">
          <div class="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-6">
            <h3 class="text-lg font-semibold text-charcoal dark:text-white mb-3">
              {{ $t('app.reservationSmsToLabel') }}
            </h3>
            <p class="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {{ $t('app.reservationSmsToHint') }}
            </p>
            <div class="flex flex-col sm:flex-row gap-3">
              <input
                v-model.trim="reservationSmsToEdit"
                type="tel"
                class="flex-1 min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white placeholder:text-slate-400"
                :placeholder="$t('app.reservationSmsToPlaceholder')"
                :aria-label="$t('app.reservationSmsToLabel')"
              >
              <AppButton
                variant="primary"
                class="min-h-[44px] shrink-0"
                :disabled="savingSmsTo || reservationSmsToEdit === (selectedRestaurant.reservation_sms_to ?? '')"
                @click="saveReservationSmsTo"
              >
                {{ savingSmsTo ? $t('app.loading') : $t('app.save') }}
              </AppButton>
            </div>
            <p v-if="smsToError" class="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{{ smsToError }}</p>
          </div>

          <!-- Visual calendar -->
          <div class="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-6">
            <ReservationCalendar
              :month="calendarMonth"
              :reservations="reservations"
              :loading="loadingReservations"
              :selected-date="selectedDate"
              data-testid="reservation-calendar"
              @update:month="onCalendarMonthChange"
              @day-selected="onDaySelected"
            />
            <p v-if="!loadingReservations && !reservations.length" class="mt-3 text-sm text-slate-500 dark:text-slate-400 text-center">
              {{ $t('app.reservationsNoReservationsMonth') }}
            </p>
          </div>

          <div v-if="reservationsError" class="rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-6 text-center">
            <p class="text-red-600 dark:text-red-400 mb-3">{{ reservationsError }}</p>
            <AppButton variant="secondary" class="min-h-[44px]" @click="fetchReservations">{{ $t('app.retry') }}</AppButton>
          </div>

          <!-- Day modal: reservations for the clicked date (bottom sheet on mobile) -->
          <AppModal
            :open="dayModalOpen"
            :title="dayModalTitle"
            :description="dayModalTitle"
            :bottom-sheet="isMobile"
            test-id="reservation-day-modal"
            @close="dayModalOpen = false"
          >
            <div v-if="selectedDate" class="min-w-0 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <div
                v-if="!reservationsForSelectedDay.length"
                class="py-12 sm:py-16 flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 px-2"
              >
                <span class="material-icons text-5xl sm:text-4xl text-slate-300 dark:text-slate-600 mb-4" aria-hidden="true">event_available</span>
                <p class="text-sm font-medium text-charcoal dark:text-slate-300">{{ $t('app.reservationsListEmpty') }}</p>
                <AppButton
                  variant="primary"
                  class="min-h-[48px] mt-6 w-full max-w-[280px] sm:w-auto sm:max-w-none"
                  data-testid="add-reservation-from-day"
                  @click="openAddReservation(selectedDate)"
                >
                  {{ $t('app.addReservation') }}
                </AppButton>
              </div>
              <div v-else class="space-y-4">
                <AppButton
                  variant="primary"
                  class="min-h-[44px] w-full sm:w-auto"
                  data-testid="add-reservation-from-day"
                  @click="openAddReservation(selectedDate)"
                >
                  {{ $t('app.addReservation') }}
                </AppButton>
                <ul class="space-y-3 sm:space-y-4" role="list">
                  <li
                    v-for="res in sortedReservationsForSelectedDay"
                    :key="res.uuid"
                  >
                  <button
                    type="button"
                    class="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 p-4 min-h-[72px] sm:p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:bg-slate-100 dark:active:bg-slate-800/70"
                    :aria-label="`${formatBookingTime(res.booking_time)} — ${res.customer_name || $t('app.reservationsTableCustomer')}`"
                    data-testid="reservation-row"
                    @click="openDetailFromDayModal(res)"
                  >
                    <div class="flex items-start justify-between gap-2 mb-1">
                      <span class="text-base sm:text-lg font-semibold text-charcoal dark:text-white">{{ formatBookingTime(res.booking_time) }}</span>
                      <span
                        class="inline-flex items-center rounded-full text-xs font-medium px-2.5 py-1 shrink-0"
                        :class="statusClass(res.status)"
                      >
                        {{ statusLabel(res.status) }}
                      </span>
                    </div>
                    <p class="font-medium text-charcoal dark:text-white">
                      {{ res.customer_name || $t('app.reservationsTableCustomer') }}
                      <template v-if="res.pax">
                        <span class="text-slate-500 dark:text-slate-400 font-normal"> · {{ res.pax }} {{ res.pax === 1 ? $t('app.guest') : $t('app.guests') }}</span>
                      </template>
                    </p>
                    <p v-if="res.customer_mobile || res.reminder_sent_at" class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <template v-if="res.reminder_sent_at">{{ $t('app.reminderSent') }}</template>
                      <template v-else-if="res.customer_mobile">{{ res.customer_mobile }}</template>
                    </p>
                  </button>
                </li>
                </ul>
              </div>
            </div>
          </AppModal>

          <!-- Add reservation modal (owner: manual add, e.g. from calendar date click) -->
          <AddReservationModal
            :open="addReservationModalOpen"
            :initial-date="addReservationInitialDate"
            :restaurant-uuid="selectedRestaurantUuid"
            data-testid="add-reservation-modal"
            @close="addReservationModalOpen = false"
            @created="onReservationCreated"
          />

          <!-- Reservation detail modal -->
          <ReservationDetailModal
            :open="detailModalOpen"
            :reservation="selectedReservation"
            :updating="updatingStatus !== null"
            data-testid="reservation-detail-modal"
            @close="closeDetail"
            @approve="confirmStatus(selectedReservation, 'approved')"
            @reject="confirmStatus(selectedReservation, 'rejected')"
            @cancel="confirmRemove(selectedReservation)"
            @update="applyEdit"
          />
        </template>
      </div>
    </template>

    <!-- Mobile FAB: Add reservation -->
    <button
      v-if="isMobile && restaurants.length"
      type="button"
      class="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))] z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-primary/30 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-95 transition-transform"
      :aria-label="$t('app.addReservation')"
      data-testid="add-reservation-from-page"
      @click="openAddReservation(todayYmd)"
    >
      <span class="material-icons text-2xl" aria-hidden="true">add</span>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMediaQuery } from '@/composables/useMediaQuery'
import AppButton from '@/components/ui/AppButton.vue'
import AppModal from '@/components/ui/AppModal.vue'
import ReservationCalendar from '@/components/reservations/ReservationCalendar.vue'
import AddReservationModal from '@/components/reservations/AddReservationModal.vue'
import ReservationDetailModal from '@/components/reservations/ReservationDetailModal.vue'
import Reservation from '@/models/Reservation'
import { useOwnerRestaurantsStore } from '@/stores/ownerRestaurants'
import { useReservationsHowItWorksStore } from '@/stores/reservationsHowItWorks'
import { reservationService, restaurantService, normalizeApiError, getValidationErrors } from '@/services'
import { useToastStore } from '@/stores/toast'
import { formatDisplayDate } from '@/utils/date'

const { t } = useI18n()
const toastStore = useToastStore()
const isMobile = useMediaQuery('(max-width: 639px)')
const ownerStore = useOwnerRestaurantsStore()
const reservationsHowItWorksStore = useReservationsHowItWorksStore()

function openHowItWorks() {
  reservationsHowItWorksStore.show()
}

const selectedRestaurantUuid = ref('')
const reservationSmsToEdit = ref('')
const savingSmsTo = ref(false)
const smsToError = ref('')
const calendarMonth = ref(getCurrentMonth())
const selectedDate = ref(null)
const reservations = ref([])
const loadingReservations = ref(false)
const reservationsError = ref(null)
const updatingStatus = ref(null)
const dayModalOpen = ref(false)
const addReservationModalOpen = ref(false)
const addReservationInitialDate = ref('')
const detailModalOpen = ref(false)
const selectedReservation = ref(null)

function getCurrentMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Today Y-m-d (for calendar display). */
const todayYmd = computed(() => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
})

function onCalendarMonthChange(newMonth) {
  calendarMonth.value = newMonth
  fetchReservations()
}

const loadingRestaurants = computed(() => ownerStore.loading)
const restaurants = computed(() => ownerStore.restaurants)

const selectedRestaurant = computed(() => {
  if (!selectedRestaurantUuid.value) return null
  return restaurants.value.find((r) => r.uuid === selectedRestaurantUuid.value) ?? null
})

const reservationsForSelectedDay = computed(() => {
  if (!selectedDate.value) return []
  return reservations.value.filter((r) => (r.booking_date ?? r.date) === selectedDate.value)
})

/** Sorted by booking_time ascending (chronological) for day modal list. */
const sortedReservationsForSelectedDay = computed(() => {
  const list = [...reservationsForSelectedDay.value]
  list.sort((a, b) => {
    const tA = a.booking_time || ''
    const tB = b.booking_time || ''
    return tA.localeCompare(tB)
  })
  return list
})

const dayModalTitle = computed(() => {
  if (!selectedDate.value) return ''
  return t('app.reservationsOnDay', { date: formatSelectedDate(selectedDate.value) })
})

function statusLabel(status) {
  const key = `app.reservationStatus_${status}`
  const tVal = t(key)
  return tVal !== key ? tVal : status
}

function statusClass(status) {
  switch (status) {
    case 'pending':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
    case 'approved':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    case 'rejected':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    default:
      return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
  }
}

async function fetchReservations() {
  if (!selectedRestaurantUuid.value) return
  loadingReservations.value = true
  reservationsError.value = null
  try {
    const [y, m] = calendarMonth.value.split('-').map(Number)
    const dateFrom = `${y}-${String(m).padStart(2, '0')}-01`
    const lastDay = new Date(y, m, 0).getDate()
    const dateTo = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    const res = await reservationService.list(selectedRestaurantUuid.value, { date_from: dateFrom, date_to: dateTo })
    const list = Array.isArray(res?.data) ? res.data : []
    reservations.value = list.map((r) => Reservation.fromApi(r).toJSON())
  } catch (e) {
    reservationsError.value = normalizeApiError(e).message ?? t('app.reservationsLoadError')
  } finally {
    loadingReservations.value = false
  }
}

function formatSelectedDate(ymd) {
  return formatDisplayDate(ymd) || ymd
}

/** Format booking_time (e.g. "14:00") for display (e.g. "2:00 PM"). */
function formatBookingTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return ''
  const [h, min] = timeStr.split(':').map(Number)
  const hours = Number.isFinite(h) ? h % 24 : 0
  const mins = Number.isFinite(min) ? min : 0
  const date = new Date(2000, 0, 1, hours, mins)
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
}

function onDaySelected(ymd) {
  selectedDate.value = ymd
  dayModalOpen.value = true
}

function openAddReservation(ymd) {
  addReservationInitialDate.value = ymd ?? selectedDate.value ?? ''
  addReservationModalOpen.value = true
}

function onReservationCreated() {
  fetchReservations()
  ownerStore.refresh()
  toastStore.success(t('app.reservationCreated'))
}

function openDetail(res) {
  selectedReservation.value = res
  detailModalOpen.value = true
}

function openDetailFromDayModal(res) {
  dayModalOpen.value = false
  selectedReservation.value = res
  detailModalOpen.value = true
}

function closeDetail() {
  detailModalOpen.value = false
  selectedReservation.value = null
}

async function confirmStatus(res, status) {
  if (!res) return
  const ok = await updateStatus(res, status)
  if (ok) closeDetail()
}

/** Remove (delete) reservation permanently. */
async function confirmRemove(res) {
  if (!res?.uuid) return
  updatingStatus.value = res.uuid
  try {
    await reservationService.remove(selectedRestaurantUuid.value, res.uuid)
    const idx = reservations.value.findIndex((r) => r.uuid === res.uuid)
    if (idx !== -1) reservations.value.splice(idx, 1)
    closeDetail()
    await ownerStore.refresh()
    toastStore.success(t('app.reservationsRemoved'))
  } catch (e) {
    toastStore.error(normalizeApiError(e).message ?? t('app.failedToUpdate'))
  } finally {
    updatingStatus.value = null
  }
}

function normalizeInternationalPhone(value) {
  const s = String(value ?? '').trim().replace(/\s/g, '')
  if (!s) return ''
  if (/^\+[0-9]{10,15}$/.test(s)) return s
  if (/^[0-9]{10,15}$/.test(s)) return `+${s}`
  return s
}

async function saveReservationSmsTo() {
  const raw = reservationSmsToEdit.value.trim()
  if (raw !== '' && !raw.startsWith('+')) {
    smsToError.value = t('app.phoneMustStartWithPlus')
    return
  }
  const val = raw === '' ? '' : normalizeInternationalPhone(raw)
  if (raw !== '' && !/^\+[0-9]{10,15}$/.test(val)) {
    smsToError.value = t('app.reservationSmsToInvalid')
    return
  }
  smsToError.value = ''
  savingSmsTo.value = true
  try {
    await restaurantService.update(selectedRestaurantUuid.value, {
      reservation_sms_to: val === '' ? null : val,
    })
    await ownerStore.refresh()
    toastStore.success(t('app.reservationSmsToSaved'))
  } catch (e) {
    const errors = getValidationErrors(e)
    smsToError.value = errors.reservation_sms_to ?? normalizeApiError(e).message ?? t('app.failedToUpdate')
  } finally {
    savingSmsTo.value = false
  }
}

/**
 * @returns {Promise<boolean>} true if update succeeded
 */
async function updateStatus(res, status) {
  if (!res?.uuid) return false
  updatingStatus.value = res.uuid
  try {
    await reservationService.update(selectedRestaurantUuid.value, res.uuid, { status })
    const idx = reservations.value.findIndex((r) => r.uuid === res.uuid)
    if (idx !== -1) {
      reservations.value.splice(idx, 1, { ...reservations.value[idx], status })
    }
    selectedReservation.value = selectedReservation.value?.uuid === res.uuid ? { ...res, status } : selectedReservation.value
    await ownerStore.refresh()
    const msg = status === 'approved' ? t('app.reservationsApproved') : status === 'cancelled' ? t('app.reservationsCancelled') : t('app.reservationsRejected')
    toastStore.success(msg)
    return true
  } catch (e) {
    toastStore.error(normalizeApiError(e).message ?? t('app.failedToUpdate'))
    return false
  } finally {
    updatingStatus.value = null
  }
}

async function applyEdit(payload) {
  const res = selectedReservation.value
  if (!res?.uuid) return
  updatingStatus.value = res.uuid
  try {
    const { data } = await reservationService.update(selectedRestaurantUuid.value, res.uuid, payload)
    const updated = Reservation.fromApi(data).toJSON()
    const idx = reservations.value.findIndex((r) => r.uuid === res.uuid)
    if (idx !== -1) {
      reservations.value.splice(idx, 1, updated)
    }
    selectedReservation.value = updated
    await ownerStore.refresh()
    toastStore.success(t('app.reservationUpdated'))
  } catch (e) {
    toastStore.error(normalizeApiError(e).message ?? t('app.failedToUpdate'))
  } finally {
    updatingStatus.value = null
  }
}

onMounted(async () => {
  await ownerStore.refresh()
  if (restaurants.value.length === 1) {
    selectedRestaurantUuid.value = restaurants.value[0].uuid
  }
})

watch(selectedRestaurantUuid, (uuid) => {
  if (uuid && selectedRestaurant.value) {
    reservationSmsToEdit.value = selectedRestaurant.value.reservation_sms_to ?? ''
    smsToError.value = ''
    selectedDate.value = null
    fetchReservations()
  } else {
    reservations.value = []
    reservationsError.value = null
    selectedDate.value = null
  }
})
</script>
